import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import BestSellers from "../components/BestSellers";
import Reviews from "../components/Reviews";
import MolecularBackground from "../components/MolecularBackground";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { ArrowRight, ShieldCheck, FlaskConical, LineChart, Fingerprint, ShoppingBag, Volume2, VolumeX, ChevronDown } from "lucide-react";
import { TrustSeal } from "../components/TrustSeal";

const FAQS = [
  { q: "What is Bio Labz?", a: "Regeneration Bio Labz supplies research compounds sourced with an emphasis on identity, purity, consistency and traceability. Products are intended for laboratory research use only." },
  { q: "How are your products tested?", a: "Each production batch is designed to be paired with third-party analytical testing (identity and purity). Certificates of Analysis are attached to products as they clear release. Where a COA is not yet available, the product is marked as pending." },
  { q: "Do you ship internationally?", a: "Please review the shipping page for the current serviced regions and lead times. Import compliance is the buyer's responsibility." },
  { q: "Are these products medicines?", a: "No. The compounds listed on this site are for laboratory research and are not approved medicines. They are not intended for diagnostic, therapeutic or human consumption purposes." },
];

const PILLARS = [
  { icon: Fingerprint, name: "Identity", copy: "Every batch verified against reference standards to confirm the correct compound is present." },
  { icon: ShieldCheck, name: "Purity", copy: "Analytical assays quantify the target and detect contaminants. Nothing is inferred." },
  { icon: LineChart, name: "Consistency", copy: "Process controls track batch-to-batch variation so you know what you are working with." },
  { icon: FlaskConical, name: "Traceability", copy: "From vial to lot to certificate. Every unit is trackable back to its release." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [audioOn, setAudioOn] = useState(false);
  const heroVideoRef = useRef(null);
  const heroMobileRef = useRef(null);
  const heroRef = useRef(null);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  // Desktop plays the 16:9 film; phones play the 1:1 cut so the full emblem is always in frame.
  const activeHero = () => (isMobile ? heroMobileRef.current : heroVideoRef.current);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  // Force-load and play the hero video (some browsers hold off with preload=auto)
  useEffect(() => {
    const v = activeHero();
    if (!v) return;
    v.muted = true;
    try { v.load(); } catch { /* some browsers throw if load() is called mid-fetch; play() below still works */ }
    const start = () => v.play().catch(() => {});
    if (v.readyState >= 2) start();
    else v.addEventListener("canplay", start, { once: true });
    return () => v.removeEventListener("canplay", start);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Where the letterboxed (object-contain) video content starts, as % of hero width — drives the edge blend mask.
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const update = () => {
      const { clientWidth: w } = el;
      const vh = (heroVideoRef.current?.clientHeight || el.clientHeight) * 1.02;
      const contentW = Math.min(w, vh * (16 / 9));
      const edge = Math.max(0, ((w - contentW) / 2 / w) * 100);
      el.style.setProperty("--bl-hero-edge", `${edge.toFixed(2)}%`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toggleAudio = () => {
    const v = activeHero();
    if (!v) return;
    if (audioOn) { v.muted = true; setAudioOn(false); }
    else {
      v.muted = false; v.volume = 0.55;
      v.play().catch(() => {});
      setAudioOn(true);
    }
  };

  const featured = products.slice(0, 6);

  return (
    <div className="bg-black">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero" className="bl-hero-bg relative h-[calc(100vw+5.5rem)] md:h-[calc(92svh+4.5rem)] max-h-[560px] md:max-h-none md:min-h-[772px] w-full overflow-hidden mt-[calc(4rem+2.25rem)] lg:mt-[calc(5rem+2.25rem)]">
        {/* Ambient blur of the same footage fills the letterbox strips so the hero reads as one continuous scene (desktop only) */}
        <video
          aria-hidden tabIndex={-1}
          autoPlay muted loop playsInline preload="metadata"
          className="bl-hero-ambient hidden md:block"
        >
          <source src="/assets/hero.mp4" type="video/mp4" />
        </video>
        <div className="bl-hero-vignette hidden md:block" />

        {/* Main video centerpiece — contain so the whole vial + emblem stays in frame; nudged down to leave a small gap under the banner */}
        <video
          data-testid="hero-video"
          ref={heroVideoRef}
          autoPlay muted loop playsInline preload="auto"
          poster="/assets/hero-poster.jpg"
          className="bl-hero-video hidden md:block absolute inset-x-0 md:top-5 w-full md:h-[calc(100%-4.5rem-1.25rem)] object-contain md:scale-[1.02]"
        >
          <source src="/assets/hero.mp4" type="video/mp4" />
        </video>
        <video
          data-testid="hero-video-mobile"
          ref={heroMobileRef}
          autoPlay muted loop playsInline preload="auto"
          poster="/assets/hero-mobile-poster.jpg"
          className="md:hidden absolute inset-x-0 top-0 w-full aspect-square h-auto object-contain"
        >
          <source src="/assets/hero-mobile.mp4" type="video/mp4" />
        </video>

        {/* Sound toggle */}
        <button
          onClick={toggleAudio}
          data-testid="hero-sound-toggle"
          aria-label={audioOn ? "Mute" : "Unmute"}
          className="absolute bottom-[6rem] md:bottom-auto md:top-24 right-4 md:right-8 z-20 inline-flex items-center gap-2 border border-white/15 hover:border-[color:var(--bl-blue)] bg-black/40 backdrop-blur-sm px-3 py-2 font-mono text-[10px] tracking-[0.28em] uppercase text-white/80 hover:text-[color:var(--bl-blue)] transition-colors"
        >
          {audioOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {audioOn ? "Sound" : "Muted"}
        </button>

        {/* Very light vignette only where nav sits so top labels stay readable — keeps colour intact */}
        <div className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(2,6,15,0.55) 0%, rgba(2,6,15,0) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{ background: "linear-gradient(0deg, rgba(2,6,15,0.85) 0%, rgba(2,6,15,0) 100%)" }} />

        {/* Tab row — Shop | Scroll | Standards — sits in the band between the picture and the divider line */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-x-4 bottom-5 md:inset-x-0 md:mx-auto md:bottom-4 md:w-[560px] z-20 flex gap-3"
        >
          <Link to="/shop" data-testid="hero-cta-shop" className="bl-btn primary flex-1 py-3 text-[10px] md:text-[11px]">Shop</Link>
          <button
            type="button" data-testid="hero-cta-scroll"
            onClick={() => window.scrollTo({ top: (heroRef.current?.offsetTop || 0) + (heroRef.current?.offsetHeight || 0) - 64, behavior: "smooth" })}
            className="bl-btn flex-1 py-3 text-[10px] md:text-[11px] inline-flex items-center gap-2"
          >
            Scroll <ChevronDown size={12} />
          </button>
          <Link to="/science" data-testid="hero-cta-standards" className="bl-btn flex-1 py-3 text-[10px] md:text-[11px]">Standards</Link>
        </motion.div>
        <TrustSeal compact className="absolute right-4 top-4 md:right-6 md:top-6 z-20" />
      </section>

      {/* Marquee band — pill tabs */}
      <section className="relative border-y border-white/5 bg-[color:var(--bl-ink)] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-2">
          {["Identity Verified","Third-Party Tested","Traceable Lots","Australian Research Supply","Precision At Every Stage"].map(t => (
            <span
              key={t}
              className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60 border border-white/10 px-3 py-1.5 hover:border-[color:var(--bl-blue)] hover:text-[color:var(--bl-blue)] transition-colors"
            >
              {t}
            </span>
          ))}
        </div>
      </section>

      <BestSellers />

      {/* Featured collection */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="bl-eyebrow">/ Collection · 01</div>
              <h2 className="bl-heading text-white text-4xl md:text-5xl lg:text-6xl mt-4 max-w-xl">The Catalogue</h2>
            </div>
            <p className="text-white/60 max-w-md">
              {products.length} research compounds spanning metabolic, healing, growth, cosmetic and cognitive research.
            </p>
          </div>
          <div className="grid gap-10 md:gap-y-16 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <div className="mt-14 text-center">
            <Link to="/shop" className="bl-btn" data-testid="home-view-all">View Every Product <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* Science pillars */}
      <section className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden" style={{ background: "radial-gradient(ellipse 60% 50% at 80% 20%, rgba(18,70,170,0.28) 0%, transparent 60%), linear-gradient(180deg, #02060F 0%, #061125 100%)" }}>
        <MolecularBackground />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="bl-eyebrow text-[color:var(--bl-blue)]">/ Standards</div>
            <h2 className="bl-heading text-white text-4xl md:text-5xl lg:text-6xl mt-4">
              Science doesn't guess.<br/>It measures. It tests. It verifies.
            </h2>
            <p className="text-white/60 mt-6 max-w-xl">
              Four pillars govern how every batch reaches our catalogue. Nothing is fabricated. Nothing is assumed.
            </p>
          </div>
          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bl-glass p-8 border border-white/5"
                data-testid={`pillar-${p.name.toLowerCase()}`}
              >
                <p.icon size={22} className="text-[color:var(--bl-blue)]" />
                <div className="mt-8 font-display uppercase tracking-[0.15em] text-white text-lg">{p.name}</div>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{p.copy}</p>
                <div className="mt-6 font-mono text-[10px] tracking-[0.25em] text-white/30 uppercase">0{i+1} / 04</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing / traceability journey */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="bl-eyebrow text-[color:var(--bl-gold)]">/ Testing · Chain of Custody</div>
          <h2 className="bl-heading text-white text-4xl md:text-5xl lg:text-6xl mt-4 max-w-3xl">Quality you can trace.</h2>

          <div className="mt-16 grid gap-10 lg:grid-cols-5">
            {[
              "Vial",
              "Batch ID",
              "Independent Analysis",
              "Certificate of Analysis",
              "Release"
            ].map((step, i) => (
              <div key={step} className="relative">
                <div className="font-mono text-[10px] tracking-[0.28em] text-white/40 uppercase">Step 0{i+1}</div>
                <div className="mt-3 font-display uppercase text-xl text-white">{step}</div>
                {i < 4 && (
                  <div className="hidden lg:block absolute top-4 right-[-16px] w-8 h-px bg-white/10" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-14 bl-glass border border-[color:var(--bl-blue)]/20 p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between">
            <div>
              <div className="bl-eyebrow text-[color:var(--bl-blue)]">Live COA Program</div>
              <div className="mt-2 font-display text-white text-2xl uppercase">Certificates arrive as batches clear release.</div>
              <p className="text-white/60 mt-3 max-w-xl">
                Where a certificate has not yet cleared review, the product displays a pending status. We do not publish fabricated results.
              </p>
            </div>
            <Link to="/testing" className="bl-btn gold" data-testid="testing-cta">See Testing Details</Link>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(11,27,61,0.6), transparent 60%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">/ Inner Circle</div>
            <h2 className="bl-heading text-white text-4xl md:text-5xl lg:text-6xl mt-4">Welcome to the Inner Circle.</h2>
            <p className="text-white/60 mt-6 max-w-xl">
              A member account unlocks order history, research updates and access to future launches. Membership is complimentary at registration.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="bl-btn gold" data-testid="membership-cta">Become a Member</Link>
              <Link to="/login" className="bl-btn" data-testid="membership-signin">Sign In</Link>
            </div>
          </div>
          <div className="relative aspect-square max-w-md ml-auto">
            <div className="absolute inset-0 bl-glass border border-[color:var(--bl-gold)]/25 flex items-center justify-center">
              <img src="/assets/emblem.png" alt="" className="w-3/4 opacity-90 bl-spin-slow" style={{ filter: "drop-shadow(0 0 60px rgba(212,175,55,0.15))" }} />
            </div>
            <div className="absolute -bottom-6 -left-6 bl-glass border border-white/10 px-5 py-3">
              <div className="font-mono text-[10px] tracking-[0.28em] text-white/50 uppercase">Member ID</div>
              <div className="font-display text-white mt-1">— Assigned on Join —</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <Reviews />

      {/* Founder note */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[860px] mx-auto px-6 lg:px-10">
          <div className="bl-eyebrow">/ Note from the Team</div>
          <blockquote className="mt-8 font-display uppercase tracking-tight text-white text-2xl md:text-3xl leading-[1.35]">
            "We started Bio Labz because the peptide research market was full of unmarked vials, unverifiable claims and marketing that outpaced the science.
            We wanted the opposite — traceable batches, third-party analytics, and a catalogue that says only what can be evidenced.
            Nothing on this site is here because it sounded impressive. It's here because a lab report exists, or the label is honest about the fact that it doesn't yet."
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center">
              <img src="/assets/emblem.png" alt="" className="w-6 opacity-80" />
            </div>
            <div>
              <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white">— The Bio Labz Team</div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-1">Regeneration · Australia</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <div className="bl-eyebrow">/ FAQ</div>
          <h2 className="bl-heading text-white text-4xl md:text-5xl mt-4">Answers.</h2>
          <div className="mt-10">
            <Accordion type="single" collapsible className="divide-y divide-white/5">
              {FAQS.map((f) => (
                <AccordionItem key={f.q} value={`item-${f.q}`} className="border-white/5">
                  <AccordionTrigger data-testid={`faq-trigger-${f.q}`} className="text-left text-white font-display uppercase tracking-tight text-lg hover:no-underline hover:text-[color:var(--bl-blue)]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 pb-6 pt-2 text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
